import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { X, Locate, ArrowLeft, Upload } from "lucide-react";
import AppShell from "@/components/AppShell";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { LISTING } from "@/constants/testIds";

// ---------------------------- Category dictionaries ----------------------------
const CATS = {
  farmer: {
    product: ["Vegetables", "Grains", "Fruits", "Milk", "Eggs", "Honey", "Seeds", "Livestock", "Organic", "Other"],
    service: ["Tractor Service", "Ploughing", "Sowing", "Harvesting", "Irrigation", "Water Pump Service", "Tractor + Driver", "Harvester", "Farm Labour", "Other Agricultural Service"],
    property: ["Land", "Agricultural Land", "House", "Shop", "Building", "Other"],
  },
  day_worker: {
    product: ["Handmade", "Furniture", "Vehicle", "Tools", "Electronics", "Household", "Other"],
    service: ["Driving", "Construction", "Mason", "Electrician", "Plumber", "Painter", "Carpenter", "Labour", "Mechanic", "Cleaning", "Repair", "Other"],
    property: ["Land", "House", "Shop", "Building", "Room", "Other"],
  },
  investor: {
    project: ["Film / Media", "Agriculture", "Technology", "Startup", "Manufacturing", "Retail", "Real Estate", "Education", "Food", "Healthcare", "Services", "Other"],
    product: ["General", "Electronics", "Vehicles", "Machinery", "Other"],
    service: ["Consulting", "Legal", "Financial", "Marketing", "Design", "Other"],
    property: ["Land", "Plot", "House", "Shop", "Building", "Commercial", "Other"],
  },
};

const PRICING_TYPES = {
  farmer_service: [
    { k: "hour",  l: "Per Hour" },
    { k: "day",   l: "Per Day" },
    { k: "acre",  l: "Per Acre" },
    { k: "trip",  l: "Per Trip" },
    { k: "fixed", l: "Fixed Price" },
  ],
  day_worker_service: [
    { k: "hour",  l: "Per Hour" },
    { k: "day",   l: "Per Day" },
    { k: "week",  l: "Per Week" },
    { k: "month", l: "Per Month" },
    { k: "fixed", l: "Fixed Price" },
  ],
};

const PRODUCT_UNITS = ["kg", "quintal", "ton", "litre", "piece", "dozen", "other"];
const AREA_UNITS   = ["sq_ft", "acre", "gunta", "cent"];
const NEED_TYPES = [
  { k: "investor",  l: "Investor" },
  { k: "funding",   l: "Funding" },
  { k: "producer",  l: "Producer" },
  { k: "partner",   l: "Business Partner" },
  { k: "cofounder", l: "Co-founder" },
  { k: "loan",      l: "Loan / Financial Support" },
  { k: "other",     l: "Other" },
];
const CONTACT_PREFS = ["phone", "whatsapp", "message"];

// -------------------------------- Component ------------------------------------
export default function PostListing() {
  const params = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const editingId = params.editingId;
  const isEditing = !!editingId;

  // Post kind (product/service/property/project) — from URL or from loaded listing.
  const [kind, setKind] = useState(params.kind || "product");
  const roleKey = user?.role || "farmer";
  const categoriesForKind = CATS[roleKey]?.[kind] || ["Other"];

  const [form, setForm] = useState({
    name: "",
    category: categoriesForKind[0],
    subcategory: "",
    custom_category: "",
    description: "",
    price: "",
    pricing_type: kind === "service" ? "day" : "fixed",
    unit: "kg",
    quantity: "",
    // Location
    state: user?.state || "",
    city: user?.district || "",
    village: user?.village || "",
    landmark: "",
    location: "",
    // Service
    experience: "",
    skills: "",
    service_area_km: 10,
    availability: "",
    available_time: "",
    available_from: "",
    // Property
    property_type: "Land",
    listing_purpose: "sale",
    area_value: "",
    area_unit: "sq_ft",
    // Project
    sector: "Agriculture",
    need_type: "investor",
    business_model: "",
    amount_required: "",
    timeline: "",
    // Contact / commerce
    contact_number: user?.mobile || "",
    whatsapp_number: user?.mobile || "",
    contact_preference: "phone",
    delivery_available: false,
    delivery_charges: "",
    pickup_available: true,
    expiry_days: 30,
  });
  const [images, setImages] = useState([]);
  const [projectPdf, setProjectPdf] = useState("");
  const [projectPdfName, setProjectPdfName] = useState("");
  const [pms, setPms] = useState(["UPI", "COD"]);
  const [locMode, setLocMode] = useState("manual");
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [locating, setLocating] = useState(false);
  const [busy, setBusy] = useState(false);

  // When kind changes, reset category to a valid one
  useEffect(() => {
    setForm((f) => ({
      ...f,
      category: categoriesForKind.includes(f.category) ? f.category : categoriesForKind[0],
      pricing_type: kind === "service" ? (f.pricing_type || "day") : "fixed",
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  // Load existing listing (edit mode)
  useEffect(() => {
    if (!isEditing) return;
    api.get(`/listings/${editingId}`).then((r) => {
      const d = r.data;
      setKind(d.kind || "product");
      setForm((f) => ({
        ...f,
        name: d.name || "",
        category: d.category || f.category,
        subcategory: d.subcategory || "",
        description: d.description || "",
        price: d.price ?? "",
        pricing_type: d.pricing_type || f.pricing_type,
        unit: d.unit || f.unit,
        quantity: d.quantity ?? "",
        state: d.state || f.state, city: d.city || f.city, village: d.village || f.village,
        landmark: d.landmark || "", location: d.location || "",
        experience: d.experience || "", skills: d.skills || "",
        service_area_km: d.service_area_km || 10,
        availability: d.availability || "", available_time: d.available_time || "", available_from: d.available_from || "",
        property_type: d.property_type || "Land", listing_purpose: d.listing_purpose || "sale",
        area_value: d.area_value ?? "", area_unit: d.area_unit || "sq_ft",
        sector: d.sector || "Agriculture", need_type: d.need_type || "investor",
        business_model: d.business_model || "", amount_required: d.amount_required ?? "", timeline: d.timeline || "",
        contact_number: d.contact_number || "", whatsapp_number: d.whatsapp_number || "",
        contact_preference: d.contact_preference || "phone",
        delivery_available: !!d.delivery_available, delivery_charges: d.delivery_charges ?? "",
        pickup_available: d.pickup_available !== false,
        expiry_days: d.expiry_days ?? 30,
      }));
      setImages(d.images || []);
      setProjectPdf(d.project_pdf || "");
      setPms(d.payment_methods?.length ? d.payment_methods : ["UPI", "COD"]);
      if (d.lat && d.lng) { setCoords({ lat: d.lat, lng: d.lng }); setLocMode("live"); }
    }).catch(() => { toast.error("Could not load post"); nav("/marketplace"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId]);

  const setF = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target?.value ?? e }));

  const getLive = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not available");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocMode("live");
        setForm((f) => ({ ...f, location: `Live @ ${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}` }));
        setLocating(false); toast.success("Location captured");
      },
      (err) => { setLocating(false); toast.error("Could not get location: " + err.message); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const onImagePick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} > 8MB — skipped`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      const tid = toast.loading(`Uploading ${f.name}…`);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        // Store absolute URL so <img src> works everywhere (and public share links)
        const absolute = data.url.startsWith("http") ? data.url : `${API.replace(/\/api$/, "")}${data.url}`;
        setImages((prev) => [...prev, absolute]);
        toast.success("Photo added", { id: tid });
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed", { id: tid });
      }
    }
  };

  const onPdfPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("PDF too large (max 8 MB)");
    const fd = new FormData();
    fd.append("file", f);
    const tid = toast.loading("Uploading PDF…");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const absolute = data.url.startsWith("http") ? data.url : `${API.replace(/\/api$/, "")}${data.url}`;
      setProjectPdf(absolute);
      setProjectPdfName(f.name);
      toast.success("PDF attached", { id: tid });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed", { id: tid });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter a title/name");
    if (kind !== "project" && (!form.price || parseFloat(form.price) < 0)) return toast.error("Please enter a valid price");
    if (kind === "project" && (!form.amount_required || parseFloat(form.amount_required) < 0)) return toast.error("Please enter the amount required");
    setBusy(true);
    try {
      const payload = {
        kind,
        post_type: kind,
        name: form.name.trim(),
        category: form.category === "Other" ? (form.custom_category || "Other") : form.category,
        subcategory: form.subcategory || undefined,
        description: form.description,
        price: parseFloat(form.price || form.amount_required || 0),
        pricing_type: kind === "service" ? form.pricing_type : undefined,
        unit: kind === "product" ? form.unit : undefined,
        quantity: form.quantity ? parseInt(form.quantity) : undefined,
        location: form.location, state: form.state, city: form.city, village: form.village, landmark: form.landmark || undefined,
        lat: coords.lat, lng: coords.lng, use_live_location: locMode === "live",
        images,
        // Service fields
        experience: form.experience || undefined,
        skills: form.skills || undefined,
        service_area_km: kind === "service" ? parseInt(form.service_area_km || 0) : undefined,
        availability: form.availability || undefined,
        available_time: form.available_time || undefined,
        available_from: form.available_from || undefined,
        // Property
        property_type: kind === "property" ? form.property_type : undefined,
        listing_purpose: kind === "property" ? form.listing_purpose : undefined,
        area_value: kind === "property" && form.area_value ? parseFloat(form.area_value) : undefined,
        area_unit: kind === "property" ? form.area_unit : undefined,
        // Project
        sector: kind === "project" ? form.sector : undefined,
        need_type: kind === "project" ? form.need_type : undefined,
        business_model: kind === "project" ? form.business_model : undefined,
        amount_required: kind === "project" && form.amount_required ? parseFloat(form.amount_required) : undefined,
        timeline: kind === "project" ? form.timeline : undefined,
        project_pdf: kind === "project" ? (projectPdf || undefined) : undefined,
        // Contact / commerce
        contact_number: form.contact_number || undefined,
        whatsapp_number: form.whatsapp_number || undefined,
        contact_preference: form.contact_preference,
        delivery_available: form.delivery_available,
        delivery_charges: parseFloat(form.delivery_charges || 0),
        pickup_available: form.pickup_available,
        payment_methods: pms,
        expiry_days: parseInt(form.expiry_days || 0),
      };
      if (isEditing) {
        await api.patch(`/listings/${editingId}`, payload);
        toast.success("Post updated");
        nav(`/marketplace/${editingId}`);
      } else {
        const { data } = await api.post("/listings", payload);
        toast.success("Posted successfully");
        nav(`/marketplace/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const titleByKind = {
    product: "Post a Product", service: "Post a Service",
    property: "Post a Property", project: "Post a Project / Investment",
  }[kind];

  const priceLabel = kind === "project" ? "Amount required (₹)"
    : kind === "service" ? "Price (₹)"
    : "Price (₹)";

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-6 max-w-3xl">
        <button onClick={() => nav(-1)} data-testid="post-back" className="text-sm text-gray-500 flex items-center gap-1"><ArrowLeft className="h-4 w-4" />Back</button>
        <div className="mt-4 text-xs uppercase tracking-[0.24em] text-gray-500">{isEditing ? "Edit Listing" : titleByKind}</div>
        <h1 className="mt-1 text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">{isEditing ? "Update your post" : titleByKind}</h1>
        <p className="mt-1 text-gray-600 text-sm">You are posting as <b className={theme.text}>{theme.name}</b>. Only relevant fields for <b>{kind}</b> are shown.</p>

        <form onSubmit={submit} className="mt-8 space-y-6">
          {/* --- Section: Basics -------------------------------------------------- */}
          <Section title="Basics">
            <Field label="Title / Name *" tid={LISTING.name} value={form.name} onChange={setF("name")} placeholder={{
              product: "e.g. Fresh Alphonso Mangoes",
              service: "e.g. Tractor with driver — 45 HP",
              property: "e.g. 2 acre agricultural land near NH-4",
              project: "e.g. Film script needs producer",
            }[kind]} />
            <div>
              <label className="text-xs font-semibold text-gray-600">Category *</label>
              <select data-testid={LISTING.category} value={form.category} onChange={setF("category")}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3">
                {categoriesForKind.map((c) => <option key={c}>{c}</option>)}
              </select>
              {form.category === "Other" && (
                <input data-testid="listing-custom-category" value={form.custom_category} onChange={setF("custom_category")}
                  placeholder="Type your category"
                  className="mt-2 w-full rounded-xl border border-emerald-300 bg-emerald-50/40 px-4 py-3 text-sm" />
              )}
            </div>
            {kind !== "project" && (
              <Field label="Subcategory (optional)" value={form.subcategory} onChange={setF("subcategory")} placeholder="More specific tag" />
            )}
            <div>
              <label className="text-xs font-semibold text-gray-600">Description</label>
              <textarea data-testid={LISTING.desc} value={form.description} onChange={setF("description")} rows={3}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3" placeholder="Tell buyers what makes this special..." />
            </div>
          </Section>

          {/* --- Section: Product-specific ---------------------------------------- */}
          {kind === "product" && (
            <Section title="Quantity & Price">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Quantity" tid="listing-qty" type="number" value={form.quantity} onChange={setF("quantity")} placeholder="e.g. 100" />
                <div>
                  <label className="text-xs font-semibold text-gray-600">Unit</label>
                  <select data-testid="listing-unit" value={form.unit} onChange={setF("unit")}
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3">
                    {PRODUCT_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <Field label={priceLabel + " *"} tid={LISTING.price} type="number" value={form.price} onChange={setF("price")} />
              <Field label="Available From" type="date" value={form.available_from} onChange={setF("available_from")} />
            </Section>
          )}

          {/* --- Section: Service-specific ---------------------------------------- */}
          {kind === "service" && (
            <Section title="Service details">
              <Field label="Experience" value={form.experience} onChange={setF("experience")} placeholder="e.g. 5 years operating tractors" />
              <Field label="Skills" value={form.skills} onChange={setF("skills")} placeholder="Comma-separated skills" />
              <div>
                <label className="text-xs font-semibold text-gray-600">Service area</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[5, 10, 25, 50, 100].map((km) => (
                    <button type="button" key={km} data-testid={`service-area-${km}`}
                      onClick={() => setForm((f) => ({ ...f, service_area_km: km }))}
                      className={`px-4 py-2 rounded-full text-sm ${form.service_area_km === km ? `${theme.bg} text-white` : "bg-white border border-gray-200 text-gray-800"}`}>Within {km} km</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Pricing Type *</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(PRICING_TYPES[`${roleKey}_service`] || PRICING_TYPES.day_worker_service).map((p) => (
                    <button type="button" key={p.k} data-testid={`pricing-${p.k}`}
                      onClick={() => setForm((f) => ({ ...f, pricing_type: p.k }))}
                      className={`px-4 py-2 rounded-full text-sm ${form.pricing_type === p.k ? `${theme.bg} text-white` : "bg-white border border-gray-200 text-gray-800"}`}>{p.l}</button>
                  ))}
                </div>
              </div>
              <Field label={`${priceLabel} *`} tid={LISTING.price} type="number" value={form.price} onChange={setF("price")}
                placeholder={form.pricing_type === "fixed" ? "e.g. 5000" : `Amount per ${form.pricing_type}`} />
              <Field label="Available From" type="date" value={form.available_from} onChange={setF("available_from")} />
              <Field label="Available Time" value={form.available_time} onChange={setF("available_time")} placeholder="e.g. Mon-Sat 6 AM - 6 PM" />
            </Section>
          )}

          {/* --- Section: Property-specific --------------------------------------- */}
          {kind === "property" && (
            <Section title="Property details">
              <div>
                <label className="text-xs font-semibold text-gray-600">Property Type</label>
                <select data-testid="prop-type" value={form.property_type} onChange={setF("property_type")}
                  className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3">
                  {categoriesForKind.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Sale or Rent</label>
                <div className="mt-2 flex gap-2">
                  {[{ k: "sale", l: "For Sale" }, { k: "rent", l: "For Rent" }].map((p) => (
                    <button type="button" key={p.k} data-testid={`purpose-${p.k}`}
                      onClick={() => setForm((f) => ({ ...f, listing_purpose: p.k }))}
                      className={`px-4 py-2 rounded-full text-sm ${form.listing_purpose === p.k ? `${theme.bg} text-white` : "bg-white border border-gray-200 text-gray-800"}`}>{p.l}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Area" tid="prop-area" type="number" value={form.area_value} onChange={setF("area_value")} placeholder="e.g. 2400" />
                <div>
                  <label className="text-xs font-semibold text-gray-600">Area unit</label>
                  <select value={form.area_unit} onChange={setF("area_unit")}
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3">
                    {AREA_UNITS.map((u) => <option key={u} value={u}>{u.replace("_", " ")}</option>)}
                  </select>
                </div>
              </div>
              <Field label={`${priceLabel} *`} tid={LISTING.price} type="number" value={form.price} onChange={setF("price")} />
            </Section>
          )}

          {/* --- Section: Project-specific ---------------------------------------- */}
          {kind === "project" && (
            <Section title="Project details">
              <div>
                <label className="text-xs font-semibold text-gray-600">Sector / Industry</label>
                <select data-testid="project-sector" value={form.sector} onChange={setF("sector")}
                  className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3">
                  {categoriesForKind.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">What do you need?</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {NEED_TYPES.map((n) => (
                    <button type="button" key={n.k} data-testid={`need-${n.k}`}
                      onClick={() => setForm((f) => ({ ...f, need_type: n.k }))}
                      className={`px-4 py-2 rounded-full text-sm ${form.need_type === n.k ? `${theme.bg} text-white` : "bg-white border border-gray-200 text-gray-800"}`}>{n.l}</button>
                  ))}
                </div>
              </div>
              <Field label="Amount required (₹) *" tid="project-amount" type="number" value={form.amount_required} onChange={setF("amount_required")} placeholder="e.g. 2500000" />
              <Field label="Business / Project Model" value={form.business_model} onChange={setF("business_model")} placeholder="How will revenue / return work?" />
              <Field label="Expected Timeline" value={form.timeline} onChange={setF("timeline")} placeholder="e.g. 6 months to launch" />
              <div>
                <label className="text-xs font-semibold text-gray-600">Project Report / Proposal (PDF, optional)</label>
                {projectPdf ? (
                  <div className="mt-2 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm">
                    <span>📄</span>
                    <span className="flex-1 truncate">{projectPdfName || "attached.pdf"}</span>
                    <button type="button" onClick={() => { setProjectPdf(""); setProjectPdfName(""); }} className="text-rose-600 text-xs">Remove</button>
                  </div>
                ) : (
                  <label data-testid="project-pdf-upload" className="mt-2 flex items-center justify-center gap-2 h-16 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 hover:border-emerald-400 cursor-pointer text-xs text-gray-500">
                    <Upload className="h-4 w-4" />Tap to attach PDF (max 8 MB)
                    <input type="file" accept="application/pdf" hidden onChange={onPdfPick} />
                  </label>
                )}
              </div>
            </Section>
          )}

          {/* --- Section: Photos --------------------------------------------------- */}
          <Section title={`Photos${images.length ? ` · ${images.length} added` : ""}`}>
            <label data-testid="photo-upload-btn" className="flex items-center justify-center gap-2 h-24 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 hover:border-emerald-400 cursor-pointer text-sm text-gray-600">
              <Upload className="h-5 w-5" />{images.length === 0 ? "Tap to upload photos" : "Tap to add more photos"}
              <input type="file" accept="image/*" multiple hidden onChange={onImagePick} />
            </label>
            <p className="text-[11px] text-gray-500">Add as many as you like — up to 5 MB each. Tap the button again to pick more.</p>
            {images.length > 0 && (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {images.map((im, i) => (
                  <div key={`img-${i}-${im.slice(0, 12)}`} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 group">
                    <img src={im} alt="" className="w-full h-full object-cover" />
                    <div className="absolute bottom-1 left-1 h-5 w-5 rounded-full bg-black/60 text-white text-[10px] flex items-center justify-center">{i + 1}</div>
                    <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))}
                      className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 md:opacity-100"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* --- Section: Location ------------------------------------------------ */}
          <Section title="Location for this listing">
            <div className="flex gap-2">
              <button type="button" data-testid="loc-manual" onClick={() => setLocMode("manual")}
                className={`flex-1 px-4 py-2.5 rounded-full text-sm font-medium ${locMode === "manual" ? `${theme.bg} text-white` : "bg-white border border-gray-200 text-gray-800"}`}>Enter Manually</button>
              <button type="button" data-testid="loc-live" onClick={getLive} disabled={locating}
                className={`flex-1 px-4 py-2.5 rounded-full text-sm font-medium inline-flex items-center justify-center gap-1.5 ${locMode === "live" ? `${theme.bg} text-white` : "bg-white border border-gray-200 text-gray-800"}`}>
                <Locate className="h-4 w-4" />{locating ? "Detecting…" : "Use my location"}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="State" tid="loc-state" value={form.state} onChange={setF("state")} placeholder="e.g. Karnataka" />
              <Field label="City / District" tid="loc-city" value={form.city} onChange={setF("city")} placeholder="e.g. Ballari" />
              <Field label="Village / Area" tid="loc-village" value={form.village} onChange={setF("village")} placeholder="e.g. Toranagallu" />
            </div>
            <Field label="Landmark / Notes (optional)" value={form.landmark} onChange={setF("landmark")} placeholder="Near temple, road name, etc." />
            {coords.lat && <div className="text-[11px] text-emerald-700">📍 Live location captured ({coords.lat.toFixed(3)}, {coords.lng.toFixed(3)})</div>}
          </Section>

          {/* --- Section: Contact & Payment -------------------------------------- */}
          <Section title="Contact & Payment">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contact Number" tid={LISTING.contact} value={form.contact_number} onChange={setF("contact_number")} />
              <Field label="WhatsApp Number" tid="listing-whatsapp" value={form.whatsapp_number} onChange={setF("whatsapp_number")} placeholder="Same as contact if empty" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600">Preferred contact method</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CONTACT_PREFS.map((p) => (
                  <button type="button" key={p} data-testid={`pref-${p}`}
                    onClick={() => setForm((f) => ({ ...f, contact_preference: p }))}
                    className={`px-4 py-2 rounded-full text-sm capitalize ${form.contact_preference === p ? `${theme.bg} text-white` : "bg-white border border-gray-200 text-gray-800"}`}>{p}</button>
                ))}
              </div>
            </div>
            {(kind === "product" || kind === "property") && (
              <div>
                <label className="text-xs font-semibold text-gray-600">Payment Methods Accepted</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {["UPI", "Card", "Net Banking", "COD", "Other"].map((m) => (
                    <button key={m} type="button" onClick={() => setPms(pms.includes(m) ? pms.filter((x) => x !== m) : [...pms, m])}
                      className={`px-4 py-2 rounded-full text-sm ${pms.includes(m) ? `${theme.bg} text-white` : "bg-white border border-gray-200 text-gray-800"}`}>{m}</button>
                  ))}
                </div>
              </div>
            )}
          </Section>

          {/* --- Section: Expiry -------------------------------------------------- */}
          <Section title="Auto-delete this post">
            <div className="flex flex-wrap gap-2">
              {[
                { d: 0, l: "Never" }, { d: 1, l: "1 day" }, { d: 7, l: "1 week" },
                { d: 30, l: "1 month" }, { d: 365, l: "1 year" },
              ].map((opt) => (
                <button key={opt.d} type="button" data-testid={`expiry-${opt.d}`}
                  onClick={() => setForm((f) => ({ ...f, expiry_days: opt.d }))}
                  className={`px-4 py-2 rounded-full text-sm ${parseInt(form.expiry_days) === opt.d ? `${theme.bg} text-white` : "bg-white border border-gray-200 text-gray-800"}`}>{opt.l}</button>
              ))}
            </div>
            <p className="text-[11px] text-gray-500">Post will hide from marketplace after this — you can extend anytime.</p>
          </Section>

          <button data-testid={LISTING.submit} disabled={busy}
            className={`w-full py-4 rounded-full ${theme.bg} text-white font-semibold shadow-xl disabled:opacity-60`}>
            {busy ? (isEditing ? "Saving..." : "Publishing...") : (isEditing ? "Save Changes" : "Publish Post")}
          </button>
        </form>
      </div>
    </AppShell>
  );
}

// --------------------------- helpers -------------------------------------------
function Section({ title, children }) {
  return (
    <div className="rounded-3xl bg-white border border-gray-100 p-5 md:p-6 space-y-4">
      <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, tid, ...rest }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600">{label}</label>
      <input data-testid={tid} {...rest} className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
    </div>
  );
}
